import { NavLink, useLocation, useMatch, useNavigate } from 'react-router';
import { cn } from '@core/utils/cn';
import { Icon } from '@ui/Icon';
import { IconButton } from '@ui/IconButton';
import { ConditionalTooltip } from '@ui/ConditionalTooltip';
import { usePermissionStore } from '@core/stores/permissions.store';
import { useIsDelegatee, useSettingsAccess } from '@core/permissions/usePermission';
import { useUiPrefsStore } from '@core/stores/uiPrefs.store';
import { useIsMobileLayout } from '@core/hooks/useMediaQuery';
import {
  isNavItemVisible,
  NAV_ITEMS,
  SETTINGS_SUBITEMS,
  type NavItem,
  type NavSubItem,
} from './navItems';

/**
 * Collapsible left rail. Ports the `<aside class="sidenav">` block of
 * `main-layout.component.html` (lines 13-132).
 *
 * Two things the Angular version did that are gone here:
 *
 * 1. It called `permissionService.hasPermission(...)` and `isModuleVisible(...)` DIRECTLY
 *    FROM THE TEMPLATE, so every one of those calls re-ran on every change-detection
 *    cycle. One `usePermissionStore` subscription now covers the whole list.
 *
 * 2. It derived the active Settings sub-item by REGEX-PARSING the router URL
 *    (`url.match(/[?&]tab=([^&]+)/)`, main-layout.component.ts:59). The sub-views are
 *    real nested routes now, so `NavLink` handles active state.
 */
/**
 * One main nav row.
 *
 * ============================================================================
 * WHY THE `className` IS A STRING AND NOT `NavLink`'s FUNCTION FORM
 * ============================================================================
 *
 * A collapsed rail wraps every row in `ConditionalTooltip`, which mounts
 * `RadixTooltip.Trigger asChild`. Radix's `Slot` merges its props onto the child and merges
 * `className` by STRING JOIN — so `className={({ isActive }) => …}` was coerced to the source text of
 * the function. The rendered `class` attribute became
 * `({ isActive }) => cn("vm-nav-item", isActive && "active")`, which contains no usable token, so
 * every nav row lost ALL of its styling.
 *
 * It only showed when COLLAPSED: `ConditionalTooltip` returns its children untouched while `when` is
 * false, so an expanded rail never went through the Slot. The Settings row survived either way
 * because it is a `<button>` with a plain string className — which is why a collapsed rail showed
 * unstyled icons next to one correctly-styled, highlighted gear.
 *
 * Computing `isActive` here with `useMatch` keeps the class a string, which passes through Slot
 * intact. It lives in its own component so the hook is called once per instance rather than in a
 * loop over a permission-filtered list, whose length varies.
 *
 * `to` carries `item.search` for the link target, but `useMatch` is given the bare path — a query
 * string is not part of route matching.
 */
function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const match = useMatch({ path: item.to, end: item.exact ?? false });

  return (
    <ConditionalTooltip when={collapsed} content={item.label} side="right">
      <NavLink
        to={`${item.to}${item.search ?? ''}`}
        end={item.exact}
        className={cn('vm-nav-item', match && 'active')}
      >
        <Icon size="md" className={item.iconClassName}>
          {item.icon}
        </Icon>
        <span className="vm-nav-label">{item.label}</span>
      </NavLink>
    </ConditionalTooltip>
  );
}

export function Sidebar({
  mobileOpen = false,
  onCloseMobile,
}: {
  /** Phone only: the slide-over menu is showing. See `AppLayout`. */
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  const sidebarOpen = useUiPrefsStore((state) => state.sidebarOpen);
  const toggleSidebar = useUiPrefsStore((state) => state.toggleSidebar);
  const settingsExpanded = useUiPrefsStore((state) => state.settingsExpanded);
  const setSettingsExpanded = useUiPrefsStore((state) => state.setSettingsExpanded);

  const actions = usePermissionStore((state) => state.actions);
  const groups = usePermissionStore((state) => state.groups);
  const hasSettingsAccess = useSettingsAccess();

  /*
   * On a phone the menu is a full-width slide-over, so it always shows its labels: the desktop
   * collapse preference (an icon rail) means nothing there and must not hide them.
   */
  const isMobile = useIsMobileLayout();
  const collapsed = !sidebarOpen && !isMobile;
  const onSettingsRoute = location.pathname.startsWith('/settings');

  // The rule itself lives in `navItems.ts` so its denied branch can be tested without a restricted login.
  const isDelegatee = useIsDelegatee();

  const isVisible = (item: NavItem | NavSubItem): boolean =>
    // `isDelegatee` so Requests stays offered to someone standing in for an approver — its route
    // admits them, and a hidden link to a permitted page is the contradiction this function prevents.
    isNavItemVisible(item, actions, groups, isDelegatee);

  /**
   * `toggleSettingsMenu()` from main-layout.component.ts:96 — clicking Settings navigates
   * into the section when we are not already there, and otherwise just folds the
   * submenu. Collapsed rail always navigates, since the submenu is hidden.
   */
  const handleSettingsClick = () => {
    if (collapsed) {
      void navigate('/settings');
      return;
    }
    if (!onSettingsRoute) {
      void navigate('/settings');
      setSettingsExpanded(true);
      return;
    }
    setSettingsExpanded(!settingsExpanded);
  };

  return (
    <aside
      id="vm-main-nav"
      className="vm-sidenav"
      data-collapsed={collapsed}
      data-mobile-open={mobileOpen}
      aria-label="Main navigation"
    >
      <div className="vm-sidenav-header">
        {/* On a phone this closes the slide-over; on desktop it expands or collapses the rail. */}
        {isMobile ? (
          <IconButton label="Close menu" tone="inverse" size="sm" onClick={onCloseMobile}>
            <Icon className="text-[22px]">close</Icon>
          </IconButton>
        ) : (
          <IconButton
            label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            tone="inverse"
            size="sm"
            onClick={toggleSidebar}
            aria-expanded={sidebarOpen}
          >
            <Icon className="text-[22px]">menu</Icon>
          </IconButton>
        )}
        {!collapsed && <span className="vm-sidenav-header-text">Navigation</span>}
      </div>

      <nav className="vm-sidenav-nav">
        {NAV_ITEMS.filter(isVisible).map((item) => (
          <SidebarNavItem key={item.id} item={item} collapsed={collapsed} />
        ))}

        {hasSettingsAccess && (
          <>
            <ConditionalTooltip when={collapsed} content="Settings" side="right">
              <button
                type="button"
                onClick={handleSettingsClick}
                aria-expanded={settingsExpanded}
                className={cn('vm-nav-item', onSettingsRoute && 'active')}
              >
                <Icon size="md">settings</Icon>
                <span className="vm-nav-label">Settings</span>
                {sidebarOpen && (
                  <Icon className="vm-nav-expand">
                    {settingsExpanded ? 'expand_less' : 'expand_more'}
                  </Icon>
                )}
              </button>
            </ConditionalTooltip>

            {settingsExpanded && sidebarOpen && (
              <div className="vm-nav-submenu">
                {SETTINGS_SUBITEMS.filter(isVisible).map((subItem) => (
                  <NavLink
                    key={subItem.id}
                    to={subItem.to}
                    className={({ isActive }) => cn('vm-nav-subitem', isActive && 'active')}
                  >
                    <Icon size="xs">{subItem.icon}</Icon>
                    <span className="vm-nav-label">{subItem.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </>
        )}
      </nav>
    </aside>
  );
}
